import { useContext, useCallback, useRef } from 'react';
import {
  AddGlossaryAssetForm,
  AddEntityTypeForm,
  AddConnectionForm,
  AddConnectorDriverForm,
  AddCustomPropertyForm,
  AddCustomPropertyListValueForm,
  AddDatasetForm,
  AddDatasetGroupForm,
  AddDatasourceForm,
  AddDatatypeForm,
  AddFieldForm,
  AddAttributeUnitForm,
  AddAttributeSensitivityForm,
  AddAttributeValueForm,
  AddGlossaryForm,
  AddOidcClientForm,
  AddStatusForm,
  AddSystemForm,
  AddScheduleForm,
  AddTaskForm,
  EditProfileForm,
  AddUserForm,
  AddUserGroupForm,
} from '../forms';

import { Asset } from '../helpers';
import { SideDialogContext } from '../context/SideDialogContext';

export const useSideDialog = clearSelectedItems => {
  const { sideDialog, setSideDialog, loadingSideDialog, setLoadingSideDialog, show, setShow } =
    useContext(SideDialogContext);

  const formRef = useRef();

  const hideSideDialog = useCallback(() => {
    setShow(false);
    setSideDialog({});
  }, [setShow]);

  const showSideDialog = useCallback(
    (mode, data, onSubmit, onCancel, submitLabel, submitLabelLoading, cancelLabel) => {
      const itemCount = data.length;

      const defaultConfig = {
        mode: '',
        Content: null,
        title: '',
        data: data || [],
        submitForm: onSubmit,
        onSubmit: () =>
          formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })),
        onCancel: onCancel || hideSideDialog,
        submitLabel: submitLabel !== undefined ? submitLabel : 'Add',
        submitLabelLoading: submitLabelLoading ? submitLabelLoading : submitLabel || 'Saving...',
        cancelLabel: cancelLabel !== undefined ? cancelLabel : 'Cancel',
        formRef: formRef,
      };

      let config = {};

      switch (mode) {
        // One form serves both kinds of glossary asset. The dialog key only decides which role its
        // picker starts on — the user can change it, and on an existing asset that converts it.
        case 'addEntity':
          config = {
            mode: 'add',
            Content: AddGlossaryAssetForm,
            defaultObjectName: Asset.Entity,
            title: 'Add Glossary Asset',
          };
          break;
        case 'addChildEntity':
          config = {
            mode: 'add_child',
            Content: AddGlossaryAssetForm,
            defaultObjectName: Asset.Entity,
            title: 'Add Glossary Asset',
          };
          break;
        case 'addEntityType':
          config = {
            mode: 'add',
            Content: AddEntityTypeForm,
            title: 'Add Entity Type',
          };
          break;
        case 'addConnection':
          config = {
            mode: 'add',
            Content: AddConnectionForm,
            title: 'Add Connection',
          };
          break;
        case 'addCustomProperty':
          config = {
            mode: 'add',
            Content: AddCustomPropertyForm,
            title: 'Add Custom Property',
          };
          break;
        case 'addCustomPropertyListValue':
          config = {
            mode: 'add',
            Content: AddCustomPropertyListValueForm,
            title: 'Add Custom Property List Value',
          };
          break;
        case 'addDataset':
          config = {
            mode: 'add',
            Content: AddDatasetForm,
            title: 'Add Dataset',
          };
          break;
        case 'addChildDataset':
          config = {
            mode: 'add_child',
            Content: AddDatasetForm,
            title: 'Add Child Dataset',
          };
          break;
        case 'addDatasetGroup':
          config = {
            mode: 'add',
            Content: AddDatasetGroupForm,
            title: 'Add Dataset Group',
          };
          break;
        case 'addDatasource':
          config = {
            mode: 'add',
            Content: AddDatasourceForm,
            title: 'Add Datasource',
          };
          break;
        case 'addDatatype':
          config = {
            mode: 'add',
            Content: AddDatatypeForm,
            title: 'Add Datatype',
          };
          break;
        case 'addODBCDriver':
          config = {
            mode: 'add',
            Content: AddConnectorDriverForm,
            title: 'Add ODBC Driver',
            connectorCode: 'odbc',
          };
          break;
        case 'addOidcClient':
          config = {
            mode: 'add',
            Content: AddOidcClientForm,
            title: 'Add Client',
          };
          break;
        case 'addField':
          config = {
            mode: 'add',
            Content: AddFieldForm,
            title: 'Add Field',
          };
          break;
        case 'addChildField':
          config = {
            mode: 'add_child',
            Content: AddFieldForm,
            title: 'Add Field',
          };
          break;
        case 'addAttribute':
          config = {
            mode: 'add',
            Content: AddGlossaryAssetForm,
            defaultObjectName: Asset.Attribute,
            title: 'Add Glossary Asset',
          };
          break;
        case 'addAttributeValue':
          config = {
            mode: 'add',
            Content: AddAttributeValueForm,
            title: 'Add Attribute Value',
          };
          break;
        case 'addAttributeSensitivity':
          config = {
            mode: 'add',
            Content: AddAttributeSensitivityForm,
            title: 'Add Sensitivity Class',
          };
          break;
        case 'addAttributeUnit':
          config = {
            mode: 'add',
            Content: AddAttributeUnitForm,
            title: 'Add Attribute Unit',
          };
          break;
        case 'addGlossary':
          config = {
            mode: 'add',
            Content: AddGlossaryForm,
            title: 'Add Glossary',
          };
          break;
        case 'addStatus':
          config = {
            mode: 'add',
            Content: AddStatusForm,
            title: 'Add Asset Status',
          };
          break;
        case 'addSystem':
          config = {
            mode: 'add',
            Content: AddSystemForm,
            title: 'Add System',
          };
          break;
        case 'addSchedule':
          config = {
            mode: 'add',
            Content: AddScheduleForm,
            title: 'Add Schedule',
          };
          break;
        case 'addTask':
          config = {
            mode: 'add',
            Content: AddTaskForm,
            title: 'Add Task',
          };
          break;
        case 'addUser':
          config = {
            mode: 'add',
            Content: AddUserForm,
            title: 'Add User',
          };
          break;
        case 'addUserGroup':
          config = {
            mode: 'add',
            Content: AddUserGroupForm,
            title: 'Add User Group',
          };
          break;

        case 'editEntity':
          config = {
            mode: 'edit',
            Content: AddGlossaryAssetForm,
            defaultObjectName: Asset.Entity,
            title: itemCount > 1 ? `Edit ${itemCount} Entities` : 'Edit Entity',
            submitLabel: 'Save',
          };
          break;
        case 'editEntityType':
          config = {
            mode: 'edit',
            Content: AddEntityTypeForm,
            title: itemCount > 1 ? `Edit ${itemCount} Entity Types` : 'Edit Entity Type',
            submitLabel: 'Save',
          };
          break;
        case 'editConnection':
          config = {
            mode: 'edit',
            Content: AddConnectionForm,
            title: itemCount > 1 ? `Edit ${itemCount} Connections` : 'Edit Connection',
            submitLabel: 'Save',
          };
          break;
        case 'editCustomProperty':
          config = {
            mode: 'edit',
            Content: AddCustomPropertyForm,
            title: itemCount > 1 ? `Edit ${itemCount} Custom Properties` : 'Edit Custom Property',
            submitLabel: 'Save',
          };
          break;
        case 'editCustomPropertyListValue':
          config = {
            mode: 'edit',
            Content: AddCustomPropertyListValueForm,
            title:
              itemCount > 1
                ? `Edit ${itemCount} Custom Property List Values`
                : 'Edit Custom Property List Value',
            submitLabel: 'Save',
          };
          break;
        case 'editDataset':
          config = {
            mode: 'edit',
            Content: AddDatasetForm,
            title: itemCount > 1 ? `Edit ${itemCount} Datasets` : 'Edit Dataset',
            submitLabel: 'Save',
          };
          break;
        case 'editDatasetGroup':
          config = {
            mode: 'edit',
            Content: AddDatasetGroupForm,
            title: itemCount > 1 ? `Edit ${itemCount} Dataset Groups` : 'Edit Dataset Group',
            submitLabel: 'Save',
          };
          break;
        case 'editDatasource':
          config = {
            mode: 'edit',
            Content: AddDatasourceForm,
            title: itemCount > 1 ? `Edit ${itemCount} Datasources` : 'Edit Datasource',
            submitLabel: 'Save',
          };
          break;
        case 'editDatatype':
          config = {
            mode: 'edit',
            Content: AddDatatypeForm,
            title: itemCount > 1 ? `Edit ${itemCount} Datatypes` : 'Edit Datatype',
            submitLabel: 'Save',
          };
          break;
        case 'editODBCDriver':
          config = {
            mode: 'edit',
            Content: AddConnectorDriverForm,
            title: itemCount > 1 ? `Edit ${itemCount} Drivers` : 'Edit Driver',
            submitLabel: 'Save',
            connectorCode: 'odbc',
          };
          break;
        case 'editOidcClient':
          config = {
            mode: 'edit',
            Content: AddOidcClientForm,
            title: itemCount > 1 ? `Edit ${itemCount} Clients` : 'Edit Client',
            submitLabel: 'Save',
          };
          break;
        case 'editField':
          config = {
            mode: 'edit',
            Content: AddFieldForm,
            title: itemCount > 1 ? `Edit ${itemCount} Fields` : 'Edit Field',
            submitLabel: 'Save',
          };
          break;
        case 'editAttribute':
          config = {
            mode: 'edit',
            Content: AddGlossaryAssetForm,
            defaultObjectName: Asset.Attribute,
            title: itemCount > 1 ? `Edit ${itemCount} Attributes` : 'Edit Attribute',
            submitLabel: 'Save',
          };
          break;
        case 'editAttributeValue':
          config = {
            mode: 'edit',
            Content: AddAttributeValueForm,
            title: itemCount > 1 ? `Edit ${itemCount} Attribute Values` : 'Edit Attribute Value',
            submitLabel: 'Save',
          };
          break;
        case 'editAttributeSensitivity':
          config = {
            mode: 'edit',
            Content: AddAttributeSensitivityForm,
            title:
              itemCount > 1 ? `Edit ${itemCount} Sensitivity Classes` : 'Edit Sensitivity Class',
            submitLabel: 'Save',
          };
          break;
        case 'editAttributeUnit':
          config = {
            mode: 'edit',
            Content: AddAttributeUnitForm,
            title: itemCount > 1 ? `Edit ${itemCount} Attribute Units` : 'Edit Attribute Unit',
            submitLabel: 'Save',
          };
          break;
        case 'editGlossary':
          config = {
            mode: 'edit',
            Content: AddGlossaryForm,
            title: itemCount > 1 ? `Edit ${itemCount} Glossaries` : 'Edit Glossary',
            submitLabel: 'Save',
          };
          break;

        case 'editProfile':
          config = {
            mode: 'edit',
            Content: EditProfileForm,
            title: itemCount > 1 ? `Edit ${itemCount} Profiles` : 'Edit Profile',
            submitLabel: 'Save',
          };
          break;
        case 'editStatus':
          config = {
            mode: 'edit',
            Content: AddStatusForm,
            title: itemCount > 1 ? `Edit ${itemCount} Asset Statuses` : 'Edit Asset Status',
            submitLabel: 'Save',
          };
          break;
        case 'editSystem':
          config = {
            mode: 'edit',
            Content: AddSystemForm,
            title: itemCount > 1 ? `Edit ${itemCount} Systems` : 'Edit System',
            submitLabel: 'Save',
          };
          break;
        case 'editSchedule':
          config = {
            mode: 'edit',
            Content: AddScheduleForm,
            title: itemCount > 1 ? `Edit ${itemCount} Schedules` : 'Edit Schedule',
            submitLabel: 'Save',
          };
          break;
        case 'editTask':
          config = {
            mode: 'edit',
            Content: AddTaskForm,
            title: itemCount > 1 ? `Edit ${itemCount} Tasks` : 'Edit Task',
            submitLabel: 'Save',
          };
          break;
        case 'editUser':
          config = {
            mode: 'edit',
            Content: AddUserForm,
            title: itemCount > 1 ? `Edit ${itemCount} Users` : 'Edit User',
            submitLabel: 'Save',
          };
          break;
        case 'editUserGroup':
          config = {
            mode: 'edit',
            Content: AddUserGroupForm,
            title: itemCount > 1 ? `Edit ${itemCount} User Groups` : 'Edit User Group',
            submitLabel: 'Save',
          };
          break;
        default:
          break;
      }

      setShow(prev => true);
      setSideDialog({ ...defaultConfig, ...config });

      clearSelectedItems && mode.substring(0, 4) !== 'edit' && clearSelectedItems();
    },
    [formRef, clearSelectedItems]
  );

  return {
    showSideDialog,
    hideSideDialog,
    sideDialog,
    setSideDialog,
    loadingSideDialog,
    setLoadingSideDialog,
    show,
  };
};
